import { useState } from "react";
import { toast } from "react-toastify";
import "./MusicActions.css";

interface CSVMusicUploadFormProps {
  competitionId: number;
  event: string | {
      id :number;
      location: string;
      start_date: string;
  };
  onUploadSuccess: (newSongs: any[]) => void;
  canSeeCSVFileUpload: boolean;
  onUploadComplete: () => void;
}

const REQUIRED_COLUMNS = ["song_id", "program", "class", "style", "description"];

const CSVMusicUpload = ({ competitionId, event, onUploadSuccess, canSeeCSVFileUpload, onUploadComplete }: CSVMusicUploadFormProps) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleCSVChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.warn("File must be a CSV");
      setCsvFile(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text !== "string") {
        toast.warn("Invalid file content");
        setCsvFile(null);
        return;
      }

      const lines = text.split("\n");
      const header = lines[0].trim().split(",").map(h => h.trim());
      const missingColumns = REQUIRED_COLUMNS.filter(col => !header.includes(col));
      
      if (header.length !== REQUIRED_COLUMNS.length) {
        toast.warn(
          <div>
            CSV must have exactly {REQUIRED_COLUMNS.length} columns<br />
            Required columns: {REQUIRED_COLUMNS.join(", ")}<br />
            Found: {header.length} columns
          </div>
        );
        setCsvFile(null);
        return;
      }

      if (missingColumns.length > 0) {
        toast.warn(`CSV is missing columns: ${missingColumns.join(", ")}`);
        setCsvFile(null);
        return;
      }

      setCsvFile(file);
    };

    reader.readAsText(file);
  };

  function canonicalizeName(input: string): string {
  if (!input) return '';
  return String(input)
    .normalize('NFKD')                 // normalize unicode
    .replace(/[^\w\s]/g, '')           // remove ALL special chars
    .replace(/_/g, '')                 // remove underscores too (optional but safest)
    .replace(/\s+/g, '_')              // collapse spaces
    .trim();                           // trim edges
}

  const handleCSVSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!csvFile) {
      toast.warn("Please select a valid CSV file before submitting");
      return;
    }
      const eventId = typeof event === 'string' ? parseInt(event) : event?.id;
      
      let location = '';
      let startDate = '';
      
      if (typeof event !== 'string') {
        location = canonicalizeName(event?.location);
        startDate = event.start_date.replace(/\//g, '_');
      }

      const queryParams = new URLSearchParams({
        location: encodeURIComponent(location),
        startDate: encodeURIComponent(startDate)
      });
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      const token = localStorage.getItem("token");
      const response = await fetch (`${import.meta.env.VITE_API_URL}/admin/${competitionId}/events/${eventId}/upload-event-songs-list?${queryParams}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const result = await response.json();

      if (!response.ok) {
        toast.warn(
          <div>
            CSV upload failed<br />
            <br />
            {result.message || "Unknown error"}
          </div>
        );
      } else {
        console.log("CSV Upload API Response:", result);
        toast.info("CSV uploaded successfully!");
        onUploadSuccess(result.data.musicRecords);
        setCsvFile(null);
        if (onUploadComplete) {
          onUploadComplete();
        }
        const fileInput = document.getElementById("songsCsv") as HTMLInputElement;
        if (fileInput) fileInput.value = "";
      }
    } catch (err) {
      toast.warn(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canSeeCSVFileUpload) return null;

return (
  <div className="form-container">
    <h2 style={{ fontSize: "24px" }}>Upload Music CSV</h2>
    <form onSubmit={handleCSVSubmit}>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
      <label style={{ fontWeight: "normal", marginRight: '15px' }}> Setup CSV for Directors</label>
      <a
      href="https://docs.google.com/spreadsheets/d/1PAovRtZmi2mvV_cc2-X_3G94wp1JjyZAg8LyhlkDV9A/edit?gid=526662826#gid=526662826"
      target="_blank"
      rel="noopener noreferrer"
      > Sample Music Setup CSV </a>
      </div>
        <input type="file" id="songsCsv" accept=".csv" onChange={handleCSVChange} style={{ marginLeft: "25px" }}/>
        <button className="formsubmit__submitbtn" type="submit"  disabled={isSubmitting || !csvFile} style={{ marginLeft: "25px", width: "120px"}}>
          {isSubmitting ? "Uploading..." : "Submit"}
        </button>
    </form>
</div> );
};

export default CSVMusicUpload;